import { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { useDefaultCatch } from '@atlassian/eoc-catch';
import { errorToastProps } from '@atlassian/eoc-common';
import { useFlags } from '@atlassian/eoc-global-context';
import { useResource } from '@atlassian/eoc-rrr-helpers';

import { messages as commonMessages } from '../../common/messages';
import type { ScheduleEntity, TeamScheduleListResponse } from '../../services/endpoints/list/types';
import { endpoints as pageEndpoints } from '../../services/endpoints/page';
import type { UiPreferencesResponse } from '../../services/endpoints/page/types';
import { getSchedulesResource, getUiPreferencesResource } from '../../services/resources';
import { getSafe } from '../../utils/validators';
import { useTeamId } from '../use-team-id';

import { UI_PREFERENCES_DEFAULT_VALUES } from './constants';
import { messages } from './messages';
import type { UpdatePreferencesActionParameters } from './types';

const useUiPreferences = () => {
	const { data: schedulesResponse } = useResource(getSchedulesResource);
	const { data: uiPreferences } = useResource(getUiPreferencesResource);

	const schedules = useMemo(
		() => getSafe(() => schedulesResponse!.schedules, [] as TeamScheduleListResponse['schedules']),
		[schedulesResponse],
	);
	const schedulePreferences = useMemo(
		() =>
			getSafe(
				() => uiPreferences!.schedulePreferences,
				[] as UiPreferencesResponse['schedulePreferences'],
			),
		[uiPreferences],
	);

	const allSchedulePreferences = useMemo(
		() =>
			schedules.map(({ id }) => {
				const preference = schedulePreferences.find(
					(schedulePreference) => schedulePreference.id === id,
				);

				return {
					id,
					collapsed:
						preference?.collapsed ?? UI_PREFERENCES_DEFAULT_VALUES.schedulePreferences.collapsed,
					showAllLayers:
						preference?.showAllLayers ??
						UI_PREFERENCES_DEFAULT_VALUES.schedulePreferences.showAllLayers,
				};
			}),
		[schedulePreferences, schedules],
	);

	const uiPreferencesWithDefaults = useMemo(
		() => ({
			displayDensity: uiPreferences?.displayDensity ?? UI_PREFERENCES_DEFAULT_VALUES.displayDensity,
			scheduleOrder: uiPreferences?.scheduleOrder ?? UI_PREFERENCES_DEFAULT_VALUES.scheduleOrder,
			schedulesCollapsed:
				uiPreferences?.schedulesCollapsed ?? UI_PREFERENCES_DEFAULT_VALUES.schedulesCollapsed,
			showGaps: uiPreferences?.showGaps ?? UI_PREFERENCES_DEFAULT_VALUES.showGaps,
			schedulePreferences: allSchedulePreferences,
		}),
		[
			allSchedulePreferences,
			uiPreferences?.displayDensity,
			uiPreferences?.scheduleOrder,
			uiPreferences?.schedulesCollapsed,
			uiPreferences?.showGaps,
		],
	);

	return uiPreferencesWithDefaults;
};

const useUiPreferencesService = () => {
	const flagApi = useFlags();
	const intlApi = useIntl();
	const teamId = useTeamId();
	const defaultCatch = useDefaultCatch();

	const updatePreferences = useCallback(
		async ({ key, value }: UpdatePreferencesActionParameters) => {
			try {
				await pageEndpoints.updateUiPreferences({
					teamId,
					key,
					value,
				});
			} catch (error) {
				defaultCatch<void>(
					{
						id: 'useUiPreferencesService-updatePreferences',
						httpErrorHandler: (err) => {
							if (err.message) {
								flagApi.showFlag({
									...errorToastProps(),
									description: err.message,
									title: intlApi.formatMessage(commonMessages.error),
								});
							} else {
								flagApi.showFlag({
									...errorToastProps(),
									title: intlApi.formatMessage(messages.updatePreferencesErrorTitle),
									actions: [
										{
											content: intlApi.formatMessage(commonMessages.tryAgain),
											onClick: () => updatePreferences({ key, value }),
										},
									],
								});
							}
						},
					},
					error,
				);
			}
		},
		[defaultCatch, flagApi, intlApi, teamId],
	);

	return { updatePreferences };
};

const useSectionUiPreferences = () => {
	const { data: uiPreferences, update: updateUiPreferencesResource } =
		useResource(getUiPreferencesResource);
	const { updatePreferences: updatePreferencesService } = useUiPreferencesService();

	const partialUpdate = useCallback(
		({ key, value }: UpdatePreferencesActionParameters) => {
			updatePreferencesService({ key, value });
			updateUiPreferencesResource((prev) => ({
				...prev,
				[key]: value,
			}));
		},
		[updatePreferencesService, updateUiPreferencesResource],
	);

	const displayDensity = useMemo(
		() => uiPreferences?.displayDensity ?? UI_PREFERENCES_DEFAULT_VALUES.displayDensity,
		[uiPreferences?.displayDensity],
	);

	const schedulesCollapsed = useMemo(
		() => uiPreferences?.schedulesCollapsed ?? UI_PREFERENCES_DEFAULT_VALUES.schedulesCollapsed,
		[uiPreferences?.schedulesCollapsed],
	);

	const scheduleOrder = useMemo(
		() => uiPreferences?.scheduleOrder ?? UI_PREFERENCES_DEFAULT_VALUES.scheduleOrder,
		[uiPreferences?.scheduleOrder],
	);

	return {
		partialUpdate,
		displayDensity,
		scheduleOrder,
		schedulesCollapsed,
	};
};

const useOrderUiPreference = () => {
	const { update: updateScheduleListResource } = useResource(getSchedulesResource);
	const { data: uiPreferences } = useResource(getUiPreferencesResource);
	const { updatePreferences } = useUiPreferencesService();

	const schedulePreferences = useMemo(
		() =>
			getSafe(
				() => uiPreferences!.schedulePreferences,
				[] as UiPreferencesResponse['schedulePreferences'],
			),
		[uiPreferences],
	);

	const onDragEnd = useCallback(
		(reorderedSchedules: ScheduleEntity[]) => {
			updateScheduleListResource(() => {
				return {
					schedules: reorderedSchedules,
				};
			});

			updatePreferences({
				key: 'schedulePreferences',
				value: reorderedSchedules.map(({ id }) => {
					const preference = schedulePreferences.find(
						(schedulePreference) => schedulePreference.id === id,
					);
					return {
						id,
						collapsed:
							preference?.collapsed ?? UI_PREFERENCES_DEFAULT_VALUES.schedulePreferences.collapsed,
						showAllLayers:
							preference?.showAllLayers ??
							UI_PREFERENCES_DEFAULT_VALUES.schedulePreferences.showAllLayers,
					};
				}),
			});
		},
		[schedulePreferences, updatePreferences, updateScheduleListResource],
	);

	return { onDragEnd };
};

const useCollapseUiPreference = ({ id }: { id: string }) => {
	const { data: schedulesResponse } = useResource(getSchedulesResource);
	const { data: uiPreferences, update: updateUiPreferencesResource } =
		useResource(getUiPreferencesResource);
	const { updatePreferences } = useUiPreferencesService();

	const schedules = useMemo(
		() => getSafe(() => schedulesResponse!.schedules, [] as TeamScheduleListResponse['schedules']),
		[schedulesResponse],
	);
	const schedulePreferences = useMemo(
		() =>
			getSafe(
				() => uiPreferences!.schedulePreferences,
				[] as UiPreferencesResponse['schedulePreferences'],
			),
		[uiPreferences],
	);

	const toggleCollapsePreference = useCallback(() => {
		const updatedPreferences = schedules.map(({ id: scheduleId }) => {
			const preference = schedulePreferences.find(
				(schedulePreference) => schedulePreference.id === scheduleId,
			);

			return {
				id: scheduleId,
				collapsed:
					id === scheduleId
						? !preference?.collapsed ?? UI_PREFERENCES_DEFAULT_VALUES.schedulePreferences.collapsed
						: preference?.collapsed ?? UI_PREFERENCES_DEFAULT_VALUES.schedulePreferences.collapsed,
				showAllLayers:
					preference?.showAllLayers ??
					UI_PREFERENCES_DEFAULT_VALUES.schedulePreferences.showAllLayers,
			};
		});

		updateUiPreferencesResource((prev) => {
			return {
				...prev!,
				schedulePreferences: updatedPreferences,
			};
		});

		updatePreferences({
			key: 'schedulePreferences',
			value: updatedPreferences,
		});
	}, [id, schedulePreferences, schedules, updatePreferences, updateUiPreferencesResource]);

	const isCollapsed = useMemo(
		() =>
			schedulePreferences.find(({ id: scheduleId }) => scheduleId === id)?.collapsed ??
			UI_PREFERENCES_DEFAULT_VALUES.schedulePreferences.collapsed,
		[id, schedulePreferences],
	);

	return { toggleCollapsePreference, isCollapsed };
};

const useLayersUiPreference = ({ id }: { id: string }) => {
	const { data: schedulesResponse } = useResource(getSchedulesResource);
	const { data: uiPreferences, update: updateUiPreferencesResource } =
		useResource(getUiPreferencesResource);
	const { updatePreferences } = useUiPreferencesService();

	const schedules = useMemo(
		() => getSafe(() => schedulesResponse!.schedules, [] as TeamScheduleListResponse['schedules']),
		[schedulesResponse],
	);
	const schedulePreferences = useMemo(
		() =>
			getSafe(
				() => uiPreferences!.schedulePreferences,
				[] as UiPreferencesResponse['schedulePreferences'],
			),
		[uiPreferences],
	);

	const toggleShowAllLayers = useCallback(() => {
		const updatedPreferences = schedules.map(({ id: scheduleId }) => {
			const preference = schedulePreferences.find(
				(schedulePreference) => schedulePreference.id === scheduleId,
			);

			return {
				id: scheduleId,
				collapsed:
					preference?.collapsed ?? UI_PREFERENCES_DEFAULT_VALUES.schedulePreferences.collapsed,
				showAllLayers:
					id === scheduleId
						? !preference?.showAllLayers ??
							UI_PREFERENCES_DEFAULT_VALUES.schedulePreferences.showAllLayers
						: preference?.showAllLayers ??
							UI_PREFERENCES_DEFAULT_VALUES.schedulePreferences.showAllLayers,
			};
		});

		updateUiPreferencesResource((prev) => {
			return {
				...prev!,
				schedulePreferences: updatedPreferences,
			};
		});

		updatePreferences({
			key: 'schedulePreferences',
			value: updatedPreferences,
		});
	}, [id, schedulePreferences, schedules, updatePreferences, updateUiPreferencesResource]);

	const showAllLayers = useMemo(
		() =>
			schedulePreferences.find(({ id: scheduleId }) => scheduleId === id)?.showAllLayers ??
			UI_PREFERENCES_DEFAULT_VALUES.schedulePreferences.showAllLayers,
		[id, schedulePreferences],
	);

	return { toggleShowAllLayers, showAllLayers };
};

export {
	useUiPreferences,
	useSectionUiPreferences,
	useOrderUiPreference,
	useCollapseUiPreference,
	useLayersUiPreference,
};
